"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import useSWR from "swr";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Play, Pause, History, Youtube, Podcast } from "lucide-react";
import { usePlayer } from "@/contexts/player-context";
import { useUser } from "@/lib/context/user-context";
import { useI18n } from "@/lib/i18n";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatTime } from "@/utils/device-detection";
import { SpinnerIcon } from "@/components/ui/SpinnerIcon";

// 用于避免 hydration 不匹配的 hook
function useHasMounted() {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  return hasMounted;
}

interface HistoryItem {
  id: string;
  episodeId: string;
  episodeTitle: string;
  podcastTitle?: string;
  thumbnailUrl?: string;
  description?: string;
  ownerAvatarUrl?: string;
  userPlaybackPosition: number;
  mediaFileDuration: number;
  completed: boolean;
  lastUpdated: string;
  progressPercentage: number;
}

function getContentType(item: HistoryItem): 'video' | 'podcast' {
  // 检查episodeId是否为YouTube视频ID格式 (11位字符)
  const isYouTubeVideoId = /^[a-zA-Z0-9_-]{11}$/.test(item.episodeId);
  
  // YouTube视频通常没有podcastTitle，或者episodeId符合YouTube格式
  // 或者如果thumbnailUrl包含i.ytimg.com，那肯定是YouTube视频
  const isLikelyVideo = isYouTubeVideoId || 
                       (!item.podcastTitle && item.episodeId.length === 11) || 
                       (item.thumbnailUrl && item.thumbnailUrl.includes('i.ytimg.com'));
  
  return isLikelyVideo ? 'video' : 'podcast';
}

// SWR fetcher function - 优化：使用 API 的 limit 参数而不是前端 slice
const fetcher = async (url: string) => {
  const response = await fetch(url, {
    // 添加缓存头，但允许 SWR 控制重新验证
    headers: {
      'Cache-Control': 'no-cache',
    },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch history');
  }
  const data = await response.json();
  return (data.historyItems || []) as HistoryItem[];
};

export default function RecentlyWatchedSection() {
  const hasMounted = useHasMounted();
  const { t, lang } = useI18n() as any;
  const { user } = useUser();
  const { 
    setCurrentEpisode, 
    play, 
    pause, 
    isPlaying, 
    currentEpisode,
    openFullPlayer,
    showPlayer,
    setIsLoading: setPlayerLoading,
    closeFullPlayer,
    setCleanReadData,
  } = usePlayer();
  const isMobile = useIsMobile();
  const [loadingEpisodeId, setLoadingEpisodeId] = useState<string | null>(null);
  const [failedThumbnails, setFailedThumbnails] = useState<Set<string>>(new Set());
  
  // 只在移动端启用直接播放，桌面端跳转到 episodes 页面
  const allowDirectPlay = hasMounted && isMobile;

  // 🚀 使用 SWR 进行数据获取，自动处理缓存、去重和重新验证
  const { data: historyItems = [], isLoading, error, mutate } = useSWR<HistoryItem[]>(
    hasMounted && user?.id ? `/api/user/${user.id}/history?limit=10` : null,
    fetcher,
    {
      // 缓存配置 - 平衡性能和实时性
      revalidateOnFocus: true, // 窗口聚焦时重新验证，确保看到最新数据
      revalidateOnReconnect: true, // 网络重连时重新验证
      dedupingInterval: 2000, // 2秒内去重相同请求，避免重复请求（缩短以更快响应）
      refreshInterval: 0, // 不自动轮询
      // 错误重试配置
      errorRetryCount: 2, // 失败时重试2次
      errorRetryInterval: 1000, // 重试间隔1秒
      // 保持之前的缓存数据，即使重新验证失败
      keepPreviousData: true,
      // 初始数据加载时不显示错误，只显示 loading
      shouldRetryOnError: true,
    }
  );

  // 🔥 监听历史记录更新事件，自动刷新数据（节流处理，避免过于频繁）
  useEffect(() => {
    if (!hasMounted || !user?.id) return;

    let refreshTimeout: NodeJS.Timeout | null = null;
    let lastRefreshTime = 0;
    const MIN_REFRESH_INTERVAL = 2000; // 最少2秒间隔刷新

    const handleHistoryUpdate = () => {
      const now = Date.now();
      // 节流：如果距离上次刷新不足2秒，延迟执行
      if (now - lastRefreshTime < MIN_REFRESH_INTERVAL) {
        if (refreshTimeout) clearTimeout(refreshTimeout);
        refreshTimeout = setTimeout(() => {
          mutate();
          lastRefreshTime = Date.now();
        }, MIN_REFRESH_INTERVAL - (now - lastRefreshTime));
      } else {
        // 立即刷新
        mutate();
        lastRefreshTime = now;
      }
    };

    // 监听自定义事件：历史记录更新
    window.addEventListener('history-updated', handleHistoryUpdate);

    return () => {
      window.removeEventListener('history-updated', handleHistoryUpdate);
      if (refreshTimeout) clearTimeout(refreshTimeout);
    };
  }, [hasMounted, user?.id, mutate]);

  const loading = isLoading;

  // 🔥 播放器播放逻辑 - 支持打开 Full Dialog
  const handlePlayInBottomPlayer = useCallback(async (item: HistoryItem, shouldOpenFullPlayer = false) => {
    console.log('🎵 Playing in player:', item.episodeTitle, { shouldOpenFullPlayer });
    
    setLoadingEpisodeId(item.episodeId);
    
    // 🔥 移动端：先显示播放器并打开全屏对话框
    if (shouldOpenFullPlayer) {
      showPlayer();
      openFullPlayer();
      setPlayerLoading(true);
    }
    
    // 🔥 先设置占位数据，让用户立即看到内容
    const placeholder = {
      shareId: item.episodeId,
      title: item.episodeTitle,
      author: item.podcastTitle,
      thumbnail: item.thumbnailUrl,
      ownerAvatarUrl: item.ownerAvatarUrl,
      duration: item.mediaFileDuration,
      description: item.description,
      hydrationPending: true,
      isHydrationPlaceholder: true,
      content: {
        shareId: item.episodeId,
        title: item.episodeTitle,
        author: item.podcastTitle,
        thumbnail: item.thumbnailUrl,
        ownerAvatarUrl: item.ownerAvatarUrl,
        duration: item.mediaFileDuration,
        description: item.description,
        hydrationPending: true,
        isHydrationPlaceholder: true,
      },
    };
    
    if (shouldOpenFullPlayer) {
      setCurrentEpisode(placeholder);
      setCleanReadData(null);
    }
    
    try {
      const response = await fetch(`/api/episodes/${item.episodeId}`);
      if (!response.ok) {
        console.error('Failed to fetch episode data');
        if (shouldOpenFullPlayer) {
          closeFullPlayer();
        }
        return;
      }
      
      const apiResponse = await response.json();
      
      // 构建 episode 数据
      const shareData = apiResponse?.content ?? {};
      const shareDataContent = shareData?.content ?? {};
      
      const episodeData = {
        ...shareData,
        hydrationPending: false,
        isHydrationPlaceholder: false,
        generationRecords: apiResponse?.generationRecords,
        content: {
          ...shareDataContent,
          hydrationPending: false,
          isHydrationPlaceholder: false,
          generationRecords: apiResponse?.generationRecords,
        },
      };
      
      const startTime = item.completed ? 0 : item.userPlaybackPosition;
      setCurrentEpisode(episodeData, startTime);
      setCleanReadData(shareDataContent?.cleanRead ?? null);
      
      setTimeout(() => {
        play();
      }, 100);
    } catch (error) {
      console.error('Error playing episode:', error);
      
      if (shouldOpenFullPlayer) {
        closeFullPlayer();
      }
      
      const episodeData = {
        shareId: item.episodeId,
        hydrationPending: false,
        isHydrationPlaceholder: false,
        content: {
          shareId: item.episodeId,
          title: item.episodeTitle,
          podcastTitle: item.podcastTitle,
          author: item.podcastTitle,
          thumbnail: item.thumbnailUrl,
          duration: item.mediaFileDuration,
          description: item.description,
          hydrationPending: false,
          isHydrationPlaceholder: false,
        }
      };
      
      const startTime = item.completed ? 0 : item.userPlaybackPosition;
      setCurrentEpisode(episodeData, startTime);
      
      setTimeout(() => {
        play();
      }, 100);
    } finally {
      setLoadingEpisodeId(null);
      if (shouldOpenFullPlayer) {
        setPlayerLoading(false);
      }
    }
  }, [setCurrentEpisode, play, openFullPlayer, showPlayer, setPlayerLoading, closeFullPlayer, setCleanReadData]);

  const isCurrentEpisode = useCallback((episodeId: string) => {
    return currentEpisode?.shareId === episodeId || 
           currentEpisode?.content?.shareId === episodeId;
  }, [currentEpisode]);

  const isPlayingThisEpisode = useCallback((episodeId: string) => {
    return isCurrentEpisode(episodeId) && isPlaying;
  }, [isCurrentEpisode, isPlaying]);

  // 获取 episode 页面 URL
  const getEpisodeUrl = useCallback((episodeId: string) => {
    return `/episodes/${episodeId}`;
  }, []);

  // 处理卡片点击：桌面端跳转，移动端打开 Full Dialog
  const handleCardClick = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>, item: HistoryItem) => {
      // 桌面端：让 Link 默认跳转行为生效
      if (!allowDirectPlay) {
        return;
      }

      // 移动端：阻止默认跳转，打开 Full Dialog 播放
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
        return;
      }

      e.preventDefault();
      
      if (isCurrentEpisode(item.episodeId)) {
        // 🔥 如果是当前 episode，打开 Full Dialog 并切换播放状态
        showPlayer();
        openFullPlayer();
        if (isPlaying) pause(); else play();
      } else {
        // 🔥 移动端：打开 Full Dialog
        await handlePlayInBottomPlayer(item, true);
      }
    },
    [allowDirectPlay, isCurrentEpisode, isPlaying, pause, play, handlePlayInBottomPlayer, showPlayer, openFullPlayer]
  );

  // 🔥 优化：在 mounted 之前显示 skeleton，而不是返回 null
  // 这样用户能立即看到加载状态，提升用户体验
  const showSkeleton = !hasMounted || loading;
  const shouldShow = hasMounted && user && (loading || historyItems.length > 0 || error);

  // 如果没有用户且已加载完成，不显示组件
  if (hasMounted && !user && !loading) {
    return null;
  }

  // 如果已加载完成但没有数据且没有错误，不显示组件
  if (hasMounted && !loading && historyItems.length === 0 && !error) {
    return null;
  }

  return (
    <section className="mt-4">
      <header className="pl-2 pb-1 sm:pb-2 flex items-center justify-between">
        <h2 className="font-sans text-xl sm:text-2xl font-bold">{t('history.title', 'Recently Watched')}</h2>
        <Button className="h-auto py-1 text-sm sm:text-normal font-normal" variant="link" asChild>
          <Link href="/history">
             <span className="text-xs sm:text-normal font-normal">{t('home.episodes.viewAll', 'View All')}</span> <History className="size-3 ml-1" />
          </Link>
        </Button>
      </header>

      <div className="w-full overflow-x-auto scrollbar-hide max-w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-2rem)]">
        <div className="flex gap-1 sm:gap-4" style={{ width: 'fit-content' }}>
          {showSkeleton ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex-shrink-0 w-[160px] sm:w-[200px]">
                <div className="h-28 sm:h-36 animate-pulse rounded-lg bg-muted/40 mb-2" />
                <div className="h-4 w-3/4 animate-pulse rounded bg-muted/40 mb-1" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-muted/40" />
              </div>
            ))
          ) : (
            historyItems.map((item) => (
              <Link
                key={item.id}
                href={getEpisodeUrl(item.episodeId)}
                onClick={(e) => handleCardClick(e, item)}
                className="flex-shrink-0 w-[160px] sm:w-[200px] group border border-border/60 rounded-xl block"
              >
                <div 
                  className="relative aspect-video w-full overflow-hidden rounded-lg bg-muted border border-border/60 transition-all hover:border-primary cursor-pointer"
                >
                  {item.thumbnailUrl && !failedThumbnails.has(item.episodeId) ? (
                    <Image
                      src={item.thumbnailUrl}
                      alt={item.episodeTitle}
                      fill
                      className="object-cover transition duration-300 group-hover:scale-105"
                      sizes="(max-width: 768px) 160px, 200px"
                      loading="lazy"
                      placeholder="blur"
                      blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                      onError={() => {
                        // 图片加载失败时，将该 episodeId 加入失败列表
                        setFailedThumbnails(prev => new Set(prev).add(item.episodeId));
                      }}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-muted-foreground text-xs p-2 text-center">
                      {item.episodeTitle}
                    </div>
                  )}
                  
                  {/* Type Icon Badge - Top Right */}
                  <div className="absolute top-1 right-1 z-10">
                    {getContentType(item) === 'video' ? (
                      <div className="bg-red-600 text-white rounded-md p-1 shadow-sm bg-opacity-90">
                        <Youtube className="w-3 h-3" />
                      </div>
                    ) : (
                      <div className="bg-amber-500 text-white rounded-md p-1 shadow-sm bg-opacity-90">
                        <Podcast className="w-3 h-3" />
                      </div>
                    )}
                  </div>

                  {/* Play Button Overlay */}
                  <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${isCurrentEpisode(item.episodeId) || loadingEpisodeId === item.episodeId ? 'opacity-100 bg-black/20' : 'opacity-0 group-hover:opacity-100 bg-black/40'}`}>
                    <button
                      disabled={loadingEpisodeId === item.episodeId}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (isCurrentEpisode(item.episodeId)) {
                          // 🔥 移动端：打开 Full Dialog
                          if (allowDirectPlay) {
                            showPlayer();
                            openFullPlayer();
                          }
                          if (isPlaying) pause(); else play();
                        } else {
                          // 🔥 移动端：打开 Full Dialog
                          handlePlayInBottomPlayer(item, allowDirectPlay);
                        }
                      }}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-transform duration-200 hover:scale-110 shadow-lg
                        ${isCurrentEpisode(item.episodeId)
                          ? 'bg-foreground text-background' 
                          : 'bg-white text-black'
                        }
                      `}
                    >
                       {loadingEpisodeId === item.episodeId ? (
                          <SpinnerIcon size="sm" color="currentColor" />
                        ) : isPlayingThisEpisode(item.episodeId) ? (
                          <Pause className="w-5 h-5" />
                        ) : (
                          <Play className="w-5 h-5 ml-0.5" />
                        )}
                    </button>
                  </div>

                  {/* Progress Bar Overlay */}
                  <div className="absolute bottom-0 left-0 right-0">
                    <Progress value={item.progressPercentage} className="h-1 rounded-none bg-white/30 [&>div]:bg-primary" />
                  </div>
                  
                  {/* Time remaining badge */}
                  {!item.completed && (
                    <div className="absolute bottom-2 right-1 bg-black/80 text-white text-[10px] px-1.5 py-0.5 rounded font-medium">
                       {Math.round((item.mediaFileDuration - item.userPlaybackPosition) / 60)}m left
                    </div>
                  )}
                </div>

                <div className="mt-2 mb-1 space-y-1 px-1 sm:px-2">
                  <h3 className="line-clamp-2 text-[12px] sm:text-[14px] font-semibold leading-tight group-hover:text-primary transition-colors">
                    {item.episodeTitle}
                  </h3>
                  <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    {item.ownerAvatarUrl ? (
                      <Image
                        src={item.ownerAvatarUrl}
                        alt={item.podcastTitle || ''}
                        width={20}
                        height={20}
                        className="rounded-full w-3.5 h-3.5 sm:w-5 sm:h-5"
                        loading="lazy"
                      />
                    ) : null}
                    {item.podcastTitle && item.podcastTitle !== 'Unknown Podcast' && (
                      <p className="line-clamp-1 truncate">
                        {item.podcastTitle}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
